/* eslint-disable max-depth */
/* eslint-disable no-loop-func */
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

import { DataService } from "base/services";
import { FIELD_TYPES } from "base/constants";
// import { FormDataWrapper } from "base/form";
import { useUserStore } from "base/stores";

import { hasFeatures } from "../components/primitives/featuregating/index.jsx";
import { parseNumber } from "./number_utils/index.js";

export const NUMBER_FIELDS = [FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY];

const isValidData = (data) => data != null && data !== undefined;

const TYPE_MAP = {
  [FIELD_TYPES.TEXT]: ["string", "phoneNumber"],
  [FIELD_TYPES.NUMBER]: ["number", "float"],
  [FIELD_TYPES.CURRENCY]: ["number", "float"],
  [FIELD_TYPES.SELECT]: ["string"],
  [FIELD_TYPES.REFERENCE]: ["string"],
  [FIELD_TYPES.DATE]: ["date"],
  [FIELD_TYPES.DATE_TIME]: ["date"]
};
const FEATURE_ALLOW_SMART_ATTACHMENT_FIELD =
  "process_allow_smartattachment_field";
const SUPPORTED_FIELD_TYPES = [...Object.keys(TYPE_MAP), FIELD_TYPES.TEXT_AREA];

export const SMART_ATTACHMENT_WIDGET = {
  FILE_TYPES: ["JPG", "JPEG", "PNG", "PDF"],
  /**
   * 20 MB
   */
  MAX_SIZE: 1024 * 1024 * 20,
  MAX_COUNT: 1
};

export function hasSmartAttachmentFeature() {
  return hasFeatures([FEATURE_ALLOW_SMART_ATTACHMENT_FIELD]);
}
export function canUseSuggestions(field) {
  return (
    field &&
    hasSmartAttachmentFeature() &&
    SUPPORTED_FIELD_TYPES.includes(field.getValue("Type"))
  );
}

export async function getDropdownValues({
  accountId,
  flowType,
  flowId,
  instanceId,
  activityInstanceId,
  fieldId,
  tableId,
  tableRowId
}) {
  const BASE_URL = `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}`;
  let dropdownSourceUrl = "";
  if (tableId && tableRowId) {
    dropdownSourceUrl = `${BASE_URL}/${tableId}/${tableRowId}/${fieldId}/dropdown`;
  } else {
    dropdownSourceUrl = `${BASE_URL}/${fieldId}/dropdown`;
  }
  const [getDropdownList] = DataService.get(dropdownSourceUrl, {
    toast: false
  });
  try {
    return await getDropdownList();
  } catch (e) {
    console.error(e);
    return [];
  }
}
export async function getLookupValue({
  accountId,
  flowType,
  flowId,
  instanceId,
  activityInstanceId,
  fieldId,
  query,
  tableId,
  tableRowId
}) {
  function getLookup({ baseUrl, fieldId, tableId, tableRowId }) {
    if (tableId && tableRowId) {
      return `${baseUrl}/${tableId}/${tableRowId}/${fieldId}/lookup`;
    } else {
      return `${baseUrl}/${fieldId}/lookup`;
    }
  }
  const baseUrl = `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}`;
  const searchUrl = getLookup({
    baseUrl,
    fieldId,
    tableId,
    tableRowId
  });
  const [getLookupData] = DataService.get(searchUrl, {
    toast: false,
    query: { q: query, page_number: 1, page_size: 10 }
  });
  try {
    return await getLookupData();
  } catch (e) {
    console.error(e);
    return { Data: [] };
  }
}

export async function initializeParsing({
  flowType,
  accountId,
  flowId,
  instanceId,
  activityInstanceId,
  payload,
  isSimulate = false
}) {
  const INIT_URL = `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}/documentparser/init`;
  // eslint-disable-next-line max-len
  const SIMULATE_INIT_URL = `/${flowType}/2/${accountId}/preview/${flowId}/${instanceId}/${activityInstanceId}/documentparser/init`;

  const [initializeParsing] = DataService.post(
    isSimulate ? SIMULATE_INIT_URL : INIT_URL,
    { data: payload }
  );
  try {
    return await initializeParsing();
  } catch (e) {
    console.error(e);
    return [];
  }
}

export async function getAttachmentResult({
  flowType,
  accountId,
  flowId,
  instanceId,
  activityInstanceId,
  attachmentId
}) {
  const [getSmartAttachmentResults] = DataService.get(
    `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}/documentparser/${attachmentId}`,
    {
      toast: false
    }
  );
  try {
    return await getSmartAttachmentResults();
  } catch (e) {
    return e;
  }
}

export async function loadSuggestions({
  accountId,
  flowType,
  flowId,
  instanceId,
  activityInstanceId,
  isSimulate = false
}) {
  // eslint-disable-next-line max-len
  const GET_SUGGESTIONS_URL = `/${flowType}/2/${accountId}/${flowId}/${instanceId}/${activityInstanceId}/documentparser/suggestion`;
  // eslint-disable-next-line max-len
  const SIMULATE_GET_SUGGESTIONS_URL = `/${flowType}/2/${accountId}/preview/${flowId}/${instanceId}/${activityInstanceId}/documentparser/suggestion`;
  const [getSuggestions] = DataService.get(
    isSimulate ? SIMULATE_GET_SUGGESTIONS_URL : GET_SUGGESTIONS_URL,
    {
      toast: false
    }
  );
  try {
    return await getSuggestions();
  } catch (e) {
    console.error(e);
    return [];
  }
}
dayjs.extend(timezone);

function canSaveChanges(targetField) {
  return targetField.getValue("Type") === FIELD_TYPES.REFERENCE;
}

export function getCurrencyTypeAndValue(currencyTypes = ["USD"], value = "") {
  const [currentType, currencyValue] = value.split(":");
  const formattedCurrencyValue = parseNumber(currencyValue);
  const currencyType =
    (currentType &&
      currencyTypes.find(
        (type) => type.toUpperCase() === currentType.toUpperCase()
      )) ||
    currencyTypes.find((type) => type === "USD") ||
    currencyTypes[0];
  return { currencyType, currencyValue: formattedCurrencyValue };
}

export function resolveCurrencyType(currencyTypes = ["USD"], meta = {}) {
  const currencyType =
    (meta.currency &&
      currencyTypes.find(
        (type) => type.toUpperCase() === meta.currency.value.toUpperCase()
      )) ||
    currencyTypes.find((type) => type === "USD") ||
    currencyTypes[0];
  return currencyType;
}

async function formatSuggestionValue(
  useFormStore,
  fieldValue,
  targetField,
  flowData,
  tableId,
  tableRowId
) {
  if (targetField.getValue("Type") === FIELD_TYPES.REFERENCE) {
    await useFormStore.getState().saveChanges(useFormStore.getState().formData);
    const result = await getLookupValue({
      ...flowData,
      fieldId: targetField.getValue("Id"),
      query: fieldValue,
      tableId,
      tableRowId
    });
    return result.Data[0];
  }
  if (targetField.getValue("Type") === FIELD_TYPES.SELECT) {
    const result = await getDropdownValues({
      ...flowData,
      fieldId: targetField.getValue("Id"),
      tableId,
      tableRowId
    });

    return result.find((val) => val === fieldValue);
  }
  if (targetField.getValue("Type") === FIELD_TYPES.CURRENCY) {
    const { currencyType, currencyValue } = getCurrencyTypeAndValue(
      targetField.getValue("CurrencyTypes"),
      Array.isArray(fieldValue) ? fieldValue[0] : fieldValue
    );
    return `${currencyValue} ${currencyType}`;
  }
  if (targetField.getValue("Type") === FIELD_TYPES.DATE_TIME) {
    const { currentUser } = useUserStore.getState();
    const userTimeZone =
      (currentUser && currentUser.TimeZone._id) ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeStr = `${dayjs
      .tz(Array.isArray(fieldValue) ? fieldValue[0] : fieldValue, userTimeZone)
      .format("YYYY-MM-DDTHH:mm:00Z")} ${userTimeZone}`;
    return timeStr;
  } else {
    return Array.isArray(fieldValue) ? fieldValue[0] : fieldValue;
  }
}

export function getSuggestionList({
  results = [],
  fieldId,
  sectionName,
  rowIndex,
  tableId,
  fieldName
}) {
  function getSuggestionListForField({
    fieldId,
    fieldName,
    sectionName,
    rowIndex,
    tableId,
    result = {},
    suggestedBy = ""
  }) {
    const { tableSuggestions, formSuggestions, result: Result } = result;
    if (tableId) {
      const currentTable = tableSuggestions.find(
        (table) => table.tableId === tableId
      );
      if (
        currentTable &&
        Result[currentTable.tableName] &&
        Result[currentTable.tableName].length > 0
      ) {
        return Result[currentTable.tableName].map((row) => {
          const currentField = currentTable.fields.find(
            (field) => field.id === fieldId
          );
          if (currentField.dataType === FIELD_TYPES.CURRENCY) {
            const { currencyValue, currencyType } = getCurrencyTypeAndValue(
              currentField.formField.getValue("CurrencyTypes"),
              row[fieldName]
            );
            return {
              value: currencyValue,
              meta: { currency: { value: currencyType } },
              suggestedBy
            };
          }
          return { value: row[fieldName], suggestedBy };
        });
      } else {
        return [];
      }
    } else if (formSuggestions) {
      const currentField = formSuggestions.find(
        (field) => field.id === fieldId
      );
      if (
        currentField &&
        Result[sectionName] &&
        isValidData(Result[sectionName][currentField.name])
      ) {
        if (currentField.dataType === FIELD_TYPES.CURRENCY) {
          if (Array.isArray(Result[sectionName][currentField.name])) {
            return Result[sectionName][currentField.name].map(
              (currencySuggestion) => {
                const { currencyValue, currencyType } = getCurrencyTypeAndValue(
                  currentField.formField.getValue("CurrencyTypes"),
                  currencySuggestion
                );
                return {
                  value: currencyValue,
                  meta: { currency: { value: currencyType } },
                  suggestedBy
                };
              }
            );
          } else {
            const { currencyValue, currencyType } = getCurrencyTypeAndValue(
              currentField.formField.getValue("CurrencyTypes"),
              Result[sectionName][currentField.name]
            );
            return {
              value: currencyValue,
              meta: { currency: { value: currencyType } },
              suggestedBy
            };
          }
        } else if (Array.isArray(Result[sectionName][currentField.name])) {
          return Result[sectionName][currentField.name].map((suggestion) => ({
            value: suggestion,
            suggestedBy
          }));
        }
        return { value: Result[sectionName][currentField.name], suggestedBy };
      }
      return [];
    }
    return [];
  }

  if (fieldId && results.length) {
    const _results = results
      .map((result) =>
        getSuggestionListForField({
          fieldId,
          sectionName,
          rowIndex,
          tableId,
          result,
          fieldName,
          suggestedBy: result.suggestedBy
        })
      )
      .reduce((acc, val) => acc.concat(val), []);
    return _results;
  }
  return [];
}

/**
 * Parse given document parsed result from api and create field <=> suggestion map
 * @param {*} model
 * @param {*} data
 * @returns
 */
export function parseDocumentResults(useFormStore, result) {
  const { schema: model } = useFormStore.getState();
  const formFields = model.getList("Model", "Field");

  const formSuggestions = formFields.map((formField) => {
    const { Id: id, Name: name, Type: dataType } = formField.toJSON();
    return {
      id,
      name,
      dataType,
      sectionName: formField.getSection().currentJson.Name,
      formField: formField
    };
  });

  const tableSuggestions = model
    .getList("Model", "Model")
    .reduce((acc, childModel) => {
      const tableFields = childModel.getList("Model", "Field");
      acc.push({
        tableId: childModel.id,
        tableName: childModel.currentJson.Name,
        fields: tableFields.map((tableField) => {
          const { Id: id, Name: name, Type: dataType } = tableField.toJSON();
          return {
            id,
            name,
            dataType,
            formField: tableField
          };
        })
      });
      return acc;
    }, []);
  return {
    result: result["Result"],
    formSuggestions,
    tableSuggestions,
    suggestedBy: result["Attachment"]["name"]
  };
}

function canUpdateField(fieldId, config) {
  if (config) {
    const fieldConfig = Object.values(config).find(
      (field) => field.id === fieldId
    );
    return fieldConfig && !fieldConfig.isHidden && !fieldConfig.isReadOnly;
  }
  return false;
}

function canUpdateTableField(fieldId, config) {
  if (config) {
    const fieldConfig = config.find((field) => field.Id === fieldId);
    return (
      fieldConfig &&
      !fieldConfig.isHidden &&
      !fieldConfig.isReadOnly &&
      !fieldConfig.ReadOnly
    );
  }
  return false;
}

/**
 * Auto fill the form with given suggestion data
 * fill supported form fields and table
 * @param {*} useFormStore
 * @param {*} modelConfig
 * @param {*} model
 * @param {*} formData
 * @param {*} param4
 */
export async function updateFormWithSuggestions(
  useFormStore,
  parsedData,
  flowData
) {
  const { result, formSuggestions, tableSuggestions } = parsedData;
  const { modelConfig, schema: model, formData } = useFormStore.getState();
  for (let {
    id: fieldId,
    name: fieldLabel,
    formField: field,
    sectionName
  } of formSuggestions) {
    if (
      formData.getValue(fieldId) === "" &&
      sectionName in result &&
      fieldLabel in result[sectionName] &&
      isValidData(result[sectionName][fieldLabel]) &&
      canUpdateField(fieldId, modelConfig[model.getValue("Id")])
    ) {
      const formattedValue = await formatSuggestionValue(
        useFormStore,
        result[sectionName][fieldLabel],
        field,
        flowData
      );
      if (isValidData(formattedValue))
        useFormStore
          .getState()
          .updateField(
            Array.isArray(formattedValue) ? formattedValue[0] : formattedValue,
            field.toJSON(),
            canSaveChanges(field)
          );
    }
  }

  for (let { tableId, tableName, fields: tableFields } of tableSuggestions) {
    let tableChanges = { _id: tableId };
    if (
      result[tableName] &&
      canUpdateField(tableId, modelConfig[model.getValue("Id")])
    ) {
      for (const value of result[tableName]) {
        await useFormStore
          .getState()
          .addTableRow({ tableId, count: 1, autoSave: false });
        const rowData = Object.entries(value);
        const tableList = useFormStore.getState().formData.getList(tableId);
        const tableRow = tableList[tableList.length - 1];
        const tableRowId = tableRow?.id;
        let tableRowChanges = { _id: tableRowId };
        for (const [fieldName, fieldValue] of rowData) {
          const field = tableFields.find((field) => field.name === fieldName);
          try {
            if (
              isValidData(fieldValue) &&
              canUpdateTableField(field.id, modelConfig[tableId])
            ) {
              const formattedValue = await formatSuggestionValue(
                useFormStore,
                fieldValue,
                field.formField,
                flowData,
                tableId,
                tableRowId
              );
              if (isValidData(formattedValue)) {
                tableRowChanges[field.id] = formattedValue;
              }
            }
          } catch (e) {
            console.error("error on filling table", e);
          }
        }

        if (Object.keys(tableRowChanges).length) {
          const tableModel = `Table::${tableId}`;
          if (!tableChanges[tableModel]) {
            tableChanges[tableModel] = [];
          }
          tableChanges[tableModel].push({ ...tableRowChanges });
          tableRowChanges = {};
        }
      }
    }

    await useFormStore
      .getState()
      .saveTableChanges(
        useFormStore.getState().formData,
        tableChanges,
        tableId
      );
  }
  useFormStore.getState().saveChanges(useFormStore.getState().formData);
}
